'use client';

import { useState, useRef, useEffect } from 'react';
import { ResponsiveSection, Text, Box, Link, Grid } from '@stitch-fix/mode-react';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { useQueryErrorEffect } from '@stitch-fix/graphql-api-provider';
import Masonry from 'react-masonry-css';
import { useDebouncedCallback } from 'use-debounce';
import { useGetLookbookPreviousLooksQuery } from '../graphql/getPreviousLooks';
import PreviousGalleryLoadingPlaceholder from './PreviousGalleryLoadingPlaceholder';
import LookImage from '../LookImage';
import { MAX_AUTOMATICALLY_RESTORED_GALLERY_PAGES } from '../hooks/useGalleryRestoration';
import { useToast } from '../../../../utils/contexts/toast-provider';
import styles from './PreviousGallery.module.scss';
const PREVIOUS_LOOKS_PAGE_SIZE = 12;
const PreviousGallery = ({
  isShareUrlEnabled = false,
  recipientCreditAmount,
  onLooksCountChange,
  pageCount = 1,
  isRestorationInitialized = true,
  onPageRequest,
  onPageCountChange,
  onRestorationComplete
}: {
  isShareUrlEnabled?: boolean;
  recipientCreditAmount?: string;
  onLooksCountChange?: (count: number) => void;
  pageCount?: number;
  isRestorationInitialized?: boolean;
  onPageRequest?: (requestedPageCount: number) => void;
  onPageCountChange?: (restoredPageCount: number) => void;
  onRestorationComplete?: () => void;
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const hasStartedRestorationRef = useRef(false);
  const isRestoringRef = useRef(false);
  const isMountedRef = useRef(true);
  const loadedPageCountRef = useRef(1);
  const {
    addToast
  } = useToast();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const debouncedResize = useDebouncedCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 150);
  useEffect(() => {
    window.addEventListener('resize', debouncedResize);
    return () => window.removeEventListener('resize', debouncedResize);
  }, [debouncedResize]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isRestoringRef.current = false;
    };
  }, []);
  const {
    EventProvider,
    ref
  } = useViewEvent({
    schema: 'view',
    name: 'vision_previous_looks'
  });
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetLookbookPreviousLooksQuery({
    variables: {
      page: 1
    }
  });
  useQueryErrorEffect(error, queryError => {
    if (isRestoringRef.current) return;
    addToast({
      delay: 6000,
      status: 'error',
      message: queryError.message
    });
  });
  const looks = data?.client?.vision?.previousLooks?.nodes ?? [];
  const hasNextPage = data?.client?.vision?.previousLooks?.pageInfo?.hasNextPage ?? false;
  const canLoadMore = hasNextPage;
  useEffect(() => {
    if (loading) return;
    onLooksCountChange?.(looks.length);
  }, [loading, looks.length, onLooksCountChange]);
  useEffect(() => {
    if (!isRestorationInitialized || loading || hasStartedRestorationRef.current) {
      return;
    }
    hasStartedRestorationRef.current = true;
    const loadedPageCount = Math.max(1, Math.ceil(looks.length / PREVIOUS_LOOKS_PAGE_SIZE));
    loadedPageCountRef.current = loadedPageCount;
    const restorationPageCount = Math.min(pageCount, MAX_AUTOMATICALLY_RESTORED_GALLERY_PAGES);
    if (loadedPageCount >= restorationPageCount) {
      onRestorationComplete?.();
      if (pageCount !== restorationPageCount) {
        onPageCountChange?.(restorationPageCount);
      }
      return;
    }
    const restorePages = async () => {
      isRestoringRef.current = true;
      setIsRestoring(true);
      let lastSuccessfulPage = loadedPageCount;
      let didFail = false;
      for (let page = loadedPageCount + 1; page <= restorationPageCount; page += 1) {
        if (!isMountedRef.current) return;
        try {
          // Restoration must preserve page order so Apollo merges the same
          // sequence the user originally loaded.
          // eslint-disable-next-line no-await-in-loop
          await fetchMore({
            variables: {
              page
            }
          });
          lastSuccessfulPage = page;
          loadedPageCountRef.current = page;
        } catch {
          didFail = true;
          break;
        }
      }
      if (!isMountedRef.current) return;
      const completedPageCount = didFail ? lastSuccessfulPage : restorationPageCount;
      if (pageCount !== completedPageCount) {
        onPageCountChange?.(completedPageCount);
      }
      isRestoringRef.current = false;
      setIsRestoring(false);
      onRestorationComplete?.();
    };
    restorePages();
  }, [fetchMore, isRestorationInitialized, loading, looks.length, onPageCountChange, onRestorationComplete, pageCount]);
  const handleLoadMore = async () => {
    if (isLoadingMore || isRestoring || !canLoadMore) return;
    const nextPage = loadedPageCountRef.current + 1;
    if (onPageRequest) {
      onPageRequest(nextPage);
    } else {
      onPageCountChange?.(nextPage);
    }
    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          page: nextPage
        }
      });
      if (isMountedRef.current) loadedPageCountRef.current = nextPage;
    } catch (err) {
      if (!isMountedRef.current) return;
      onPageCountChange?.(loadedPageCountRef.current);
      console.error('Fetch more failed:', err);
      addToast({
        delay: 6000,
        status: 'error',
        message: 'Failed to fetch more looks'
      });
    } finally {
      if (isMountedRef.current) setIsLoadingMore(false);
    }
  };
  if (!isRestorationInitialized || isRestoring || loading && !looks.length) {
    return <ResponsiveSection gutter={{
      sm: 'none'
    }}>
        <Text setting="title-large" mb={1.5}>
          Previous Galleries
        </Text>
        <Grid columns={{
        sm: 2,
        md: 3,
        lg: 4
      }} mt={1.5} spacing="wide">
          <PreviousGalleryLoadingPlaceholder />
          <PreviousGalleryLoadingPlaceholder />
          <PreviousGalleryLoadingPlaceholder />
          <PreviousGalleryLoadingPlaceholder />
        </Grid>
      </ResponsiveSection>;
  }
  if (!looks.length) return null;
  return <EventProvider>
      <ResponsiveSection gutter={{
      sm: 'none'
    }}>
        <Box ref={ref}>
          <Masonry key={windowWidth} breakpointCols={{
          default: 4,
          899: 3,
          559: 2
        }} className={styles.masonryGrid} columnClassName={styles.masonryGridColumn}>
            {looks.map(look => {
            return <Box key={look.id} className={styles.masonryItem}>
                  <LookImage look={look} useSmallIcon isShareUrlEnabled={isShareUrlEnabled} recipientCreditAmount={recipientCreditAmount} />
                </Box>;
          })}
          </Masonry>

          {canLoadMore && <Box mt={1.5} style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
              <Text setting="body-medium">
                <Link as="button" disabled={isLoadingMore} onClick={handleLoadMore}>
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </Link>
              </Text>
            </Box>}
        </Box>
      </ResponsiveSection>
    </EventProvider>;
};
export default PreviousGallery;